import { Box, Button, Typography } from "@mui/material";
import { SubscriptionContext, SubscriptionTab } from "contexts/Subscription";
import { useContext } from "react";
import { Plans } from "schemas/payments";

import { PlanAction } from "features/Home/UpgradeDialog/Plan/helpers";
import { useSubscription } from "utils/useSubscription";

const FEATURES = [
  {
    icon: "fa-solid fa-bell",
    title: "Smart Notifications",
    description:
      "Email alerts when brands need review or automations need attention",
    iconBg: "purple.main",
    iconColor: "primary.main",
    borderColor: "rgba(177,179,248,0.3)",
    background:
      "linear-gradient(150deg, rgba(177, 179, 248, 0.1) 0%, rgba(177, 179, 248, 0.2) 100%)",
  },
  {
    icon: "fa-solid fa-clock",
    title: "Smart Timing",
    description:
      "Auto-send during business hours, avoid weekends for max engagement",
    iconBg: "yellow.main",
    iconColor: "primary.main",
    borderColor: "rgba(247,238,111,0.4)",
    background:
      "linear-gradient(150deg, rgba(247, 238, 111, 0.1) 0%, rgba(247, 238, 111, 0.2) 100%)",
  },
  {
    icon: "fa-solid fa-bullseye",
    title: "Brand Alignment",
    description:
      "Location alignment & exclude misaligned categories automatically",
    iconBg: "#00a63e",
    iconColor: "white",
    borderColor: "#b9f8cf",
    background:
      "linear-gradient(150deg, rgb(240, 253, 244) 0%, rgba(220, 252, 231, 0.5) 100%)",
  },
];

const STATS = [
  { value: "10x", label: "Faster" },
  { value: "24/7", label: "Auto-Pitch" },
  { value: "100%", label: "Hands-Off" },
];

const QUEUE_ROWS = [
  { text: "+15 brands", barWidth: 96 },
  { text: "+12 brands", barWidth: 80 },
];

const PITCH_ROWS = [
  { time: "Tue 9:30am", barWidth: 128 },
  { time: "Wed 2:15pm", barWidth: 112 },
  { time: "Thu 11:00am", barWidth: 144 },
];

export default function UnsubscribedView() {
  const { plans, tab } = useContext(SubscriptionContext);
  const { onPlanButtonClick } = useSubscription();

  const handleUpgrade = () => {
    const plan =
      tab === SubscriptionTab.yearly ? Plans.AUTOPILOT_YEARLY : Plans.AUTOPILOT;
    onPlanButtonClick(plan, PlanAction.upgrade);
  };

  const monthlyPrice = plans?.[Plans.AUTOPILOT]?.price;
  const yearlyPrice = plans?.[Plans.AUTOPILOT_YEARLY]?.price;
  const startingPrice = yearlyPrice ?? monthlyPrice;
  const priceDisplay = startingPrice
    ? `$${startingPrice.toFixed(2)}/month`
    : "$29.17/month";

  return (
    <Box
      sx={{
        display: "flex",
        alignItems: "flex-start",
        justifyContent: "center",
        width: "100%",
        py: 4,
      }}
    >
      <Box
        sx={{
          maxWidth: 848,
          width: "100%",
          borderRadius: "16px",
          border: "2px solid rgba(1,67,57,0.2)",
          boxShadow: "0px 25px 50px -12px rgba(0,0,0,0.25)",
          backgroundColor: "white",
          overflow: "hidden",
        }}
      >
        {/* Hero Section */}
        <Box
          sx={{
            background:
              "linear-gradient(166deg, rgb(1, 67, 57) 0%, rgba(1, 67, 57, 0.9) 100%)",
            px: 4,
            pt: 4,
            pb: 4,
            position: "relative",
            overflow: "hidden",
            textAlign: "center",
          }}
        >
          <Box
            sx={{
              position: "absolute",
              right: 0,
              top: 0,
              width: 256,
              height: 256,
              borderRadius: "50%",
              backgroundColor: "rgba(247,238,111,0.1)",
              filter: "blur(64px)",
            }}
          />
          <Box
            sx={{
              position: "absolute",
              left: 0,
              top: 16,
              width: 192,
              height: 192,
              borderRadius: "50%",
              backgroundColor: "rgba(177,179,248,0.1)",
              filter: "blur(40px)",
            }}
          />

          <Box
            sx={{
              position: "relative",
              zIndex: 1,
              display: "flex",
              justifyContent: "center",
              mb: 2,
            }}
          >
            <Box sx={{ position: "relative" }}>
              <Box
                sx={{
                  width: 56,
                  height: 56,
                  borderRadius: "50%",
                  backgroundColor: "yellow.main",
                  filter: "blur(24px)",
                  opacity: 0.5,
                  position: "absolute",
                  top: 0,
                  left: 0,
                }}
              />
              <Box
                sx={{
                  width: 56,
                  height: 56,
                  borderRadius: "50%",
                  backgroundColor: "yellow.main",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  position: "relative",
                }}
              >
                <Box
                  component="span"
                  className="fa-solid fa-crown"
                  sx={{ fontSize: 24, color: "primary.main" }}
                />
              </Box>
            </Box>
          </Box>

          <Typography
            sx={{
              position: "relative",
              zIndex: 1,
              fontWeight: 700,
              fontSize: 30,
              lineHeight: "36px",
              color: "white",
              mb: 1,
            }}
          >
            Upgrade to Autopilot
          </Typography>
          <Typography
            sx={{
              position: "relative",
              zIndex: 1,
              fontSize: 18,
              lineHeight: "28px",
              color: "rgba(255,255,255,0.8)",
            }}
          >
            Set it once. We handle everything else.
          </Typography>
        </Box>

        {/* Content Area */}
        <Box sx={{ p: 4 }}>
          {/* Workflow Card */}
          <Box
            sx={{
              border: "2px solid rgba(1,67,57,0.1)",
              borderRadius: "14px",
              overflow: "hidden",
              background:
                "linear-gradient(144deg, rgb(250, 250, 246) 0%, rgb(251, 251, 248) 25%, rgb(252, 252, 250) 50%, rgb(254, 254, 253) 75%, rgb(255, 255, 255) 100%)",
              mb: 4,
            }}
          >
            {/* Workflow Header */}
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 1.5,
                px: 3,
                py: 2,
                backgroundColor: "white",
                borderBottom: "1px solid rgba(1,67,57,0.1)",
              }}
            >
              <Box
                sx={{
                  width: 36,
                  height: 36,
                  borderRadius: "10px",
                  backgroundColor: "primary.main",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  flexShrink: 0,
                }}
              >
                <Box
                  component="span"
                  className="fa-solid fa-bolt"
                  sx={{ fontSize: 16, color: "white" }}
                />
              </Box>
              <Box>
                <Typography
                  sx={{
                    fontWeight: 700,
                    fontSize: 18,
                    lineHeight: "28px",
                    color: "primary.main",
                  }}
                >
                  Weekly Auto-Pilot Mode
                </Typography>
                <Typography
                  sx={{
                    fontSize: 14,
                    lineHeight: "20px",
                    color: "rgba(1,67,57,0.7)",
                  }}
                >
                  Set it once. We handle everything else.
                </Typography>
              </Box>
            </Box>

            {/* Workflow Steps */}
            <Box sx={{ px: 3, pt: 2.5, pb: 3 }}>
              {/* Week 1 Divider */}
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: 1,
                  mb: 3,
                }}
              >
                <Box
                  sx={{
                    flex: 1,
                    height: "1px",
                    background:
                      "linear-gradient(to right, rgba(1,67,57,0.2), transparent)",
                  }}
                />
                <Typography
                  sx={{
                    fontSize: 12,
                    fontWeight: 700,
                    color: "rgba(1,67,57,0.6)",
                    letterSpacing: "0.3px",
                    textTransform: "uppercase",
                    whiteSpace: "nowrap",
                  }}
                >
                  Week 1
                </Typography>
                <Box
                  sx={{
                    flex: 1,
                    height: "1px",
                    background:
                      "linear-gradient(to left, rgba(1,67,57,0.2), transparent)",
                  }}
                />
              </Box>

              {/* Step 1: Queue Auto-Filled */}
              <Box sx={{ display: "flex", gap: 2, mb: 3 }}>
                <Box
                  sx={{
                    width: 32,
                    height: 32,
                    borderRadius: "50%",
                    backgroundColor: "purple.main",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    flexShrink: 0,
                  }}
                >
                  <Typography
                    sx={{
                      fontWeight: 700,
                      fontSize: 14,
                      color: "white",
                      lineHeight: "20px",
                    }}
                  >
                    1
                  </Typography>
                </Box>
                <Box sx={{ flex: 1, minWidth: 0 }}>
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      gap: 1,
                      mb: 1,
                      flexWrap: "wrap",
                    }}
                  >
                    <Typography
                      sx={{
                        fontWeight: 700,
                        fontSize: 16,
                        color: "primary.main",
                        lineHeight: "24px",
                      }}
                    >
                      Monday: Queue Auto-Filled
                    </Typography>
                    <Box
                      sx={{
                        backgroundColor: "#dcfce7",
                        borderRadius: "4px",
                        px: 1,
                        py: "2px",
                        display: "flex",
                        alignItems: "center",
                        gap: 0.5,
                      }}
                    >
                      <Box
                        component="span"
                        className="fa-solid fa-check"
                        sx={{ fontSize: 10, color: "#008236" }}
                      />
                      <Typography
                        sx={{
                          fontSize: 10,
                          fontWeight: 700,
                          color: "#008236",
                          textTransform: "uppercase",
                          lineHeight: "15px",
                        }}
                      >
                        Automated
                      </Typography>
                    </Box>
                  </Box>

                  <Box
                    sx={{
                      backgroundColor: "rgba(177,179,248,0.1)",
                      border: "1px solid rgba(177,179,248,0.2)",
                      borderRadius: "10px",
                      p: 1.5,
                      display: "flex",
                      flexDirection: "column",
                      gap: 1,
                    }}
                  >
                    {QUEUE_ROWS.map((row, i) => (
                      <Box
                        key={i}
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "space-between",
                        }}
                      >
                        <Box
                          sx={{
                            display: "flex",
                            alignItems: "center",
                            gap: 1,
                          }}
                        >
                          <Box
                            sx={{
                              width: 32,
                              height: 32,
                              borderRadius: "4px",
                              backgroundColor: "rgba(177,179,248,0.3)",
                              flexShrink: 0,
                            }}
                          />
                          <Box
                            sx={{
                              width: row.barWidth,
                              height: 8,
                              borderRadius: "4px",
                              backgroundColor: "rgba(1,67,57,0.2)",
                            }}
                          />
                        </Box>
                        <Typography
                          sx={{
                            fontSize: 12,
                            color: "rgba(1,67,57,0.6)",
                            lineHeight: "16px",
                          }}
                        >
                          {row.text}
                        </Typography>
                      </Box>
                    ))}
                  </Box>
                </Box>
              </Box>

              {/* Step 2: Pitches Sent Daily */}
              <Box sx={{ display: "flex", gap: 2, mb: 3 }}>
                <Box
                  sx={{
                    width: 32,
                    height: 32,
                    borderRadius: "50%",
                    backgroundColor: "primary.main",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    flexShrink: 0,
                  }}
                >
                  <Typography
                    sx={{
                      fontWeight: 700,
                      fontSize: 14,
                      color: "white",
                      lineHeight: "20px",
                    }}
                  >
                    2
                  </Typography>
                </Box>
                <Box sx={{ flex: 1, minWidth: 0 }}>
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      gap: 1,
                      mb: 1,
                      flexWrap: "wrap",
                    }}
                  >
                    <Typography
                      sx={{
                        fontWeight: 700,
                        fontSize: 16,
                        color: "primary.main",
                        lineHeight: "24px",
                      }}
                    >
                      All Week: Pitches Sent Daily
                    </Typography>
                    <Box
                      sx={{
                        backgroundColor: "yellow.main",
                        borderRadius: "4px",
                        px: 1,
                        py: "2px",
                      }}
                    >
                      <Typography
                        sx={{
                          fontSize: 10,
                          fontWeight: 700,
                          color: "primary.main",
                          textTransform: "uppercase",
                          lineHeight: "15px",
                        }}
                      >
                        Auto-Send
                      </Typography>
                    </Box>
                  </Box>

                  <Box
                    sx={{
                      backgroundColor: "#f0fdf4",
                      border: "1px solid #b9f8cf",
                      borderRadius: "10px",
                      p: 1.5,
                      display: "flex",
                      flexDirection: "column",
                      gap: 1,
                    }}
                  >
                    {PITCH_ROWS.map((row, i) => (
                      <Box
                        key={i}
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "space-between",
                        }}
                      >
                        <Box
                          sx={{
                            display: "flex",
                            alignItems: "center",
                            gap: 1,
                          }}
                        >
                          <Box
                            component="span"
                            className="fa-solid fa-check"
                            sx={{ fontSize: 12, color: "#22c55e" }}
                          />
                          <Box
                            sx={{
                              width: row.barWidth,
                              height: 8,
                              borderRadius: "4px",
                              backgroundColor: "#b9f8cf",
                            }}
                          />
                        </Box>
                        <Typography
                          sx={{
                            fontSize: 12,
                            color: "rgba(1,67,57,0.7)",
                            lineHeight: "16px",
                          }}
                        >
                          {row.time}
                        </Typography>
                      </Box>
                    ))}
                  </Box>
                </Box>
              </Box>

              {/* Summary Callout */}
              <Box
                sx={{
                  backgroundColor: "rgba(1,67,57,0.05)",
                  borderLeft: "4px solid #014339",
                  borderRadius: "0 10px 10px 0",
                  pl: 2.5,
                  pr: 2,
                  py: 1.5,
                }}
              >
                <Box sx={{ display: "flex", gap: 1 }}>
                  <Box
                    component="span"
                    className="fa-solid fa-wand-magic-sparkles"
                    sx={{
                      fontSize: 16,
                      color: "primary.main",
                      mt: "2px",
                      flexShrink: 0,
                    }}
                  />
                  <Box>
                    <Typography
                      sx={{
                        fontWeight: 700,
                        fontSize: 16,
                        lineHeight: "24px",
                        color: "primary.main",
                      }}
                    >
                      {"You did: Nothing."}
                    </Typography>
                    <Typography
                      sx={{
                        fontSize: 14,
                        lineHeight: "22.75px",
                        color: "rgba(1,67,57,0.7)",
                      }}
                    >
                      Your automation sourced 27 brands, sent personalized
                      pitches during optimal times, and handled all follow-ups.
                      100% hands-free.
                    </Typography>
                  </Box>
                </Box>
              </Box>
            </Box>
          </Box>

          {/* Feature Cards */}
          <Box
            sx={{
              display: "flex",
              gap: 2,
              mb: 4,
              flexWrap: { xs: "wrap", sm: "nowrap" },
            }}
          >
            {FEATURES.map((feature, index) => (
              <Box
                key={index}
                sx={{
                  flex: { xs: "1 1 100%", sm: 1 },
                  p: 2,
                  borderRadius: "14px",
                  border: `1px solid ${feature.borderColor}`,
                  background: feature.background,
                }}
              >
                <Box
                  sx={{
                    width: 36,
                    height: 36,
                    borderRadius: "10px",
                    backgroundColor: feature.iconBg,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    mb: 1.5,
                  }}
                >
                  <Box
                    component="span"
                    className={feature.icon}
                    sx={{
                      fontSize: 16,
                      color: feature.iconColor,
                    }}
                  />
                </Box>
                <Typography
                  sx={{
                    fontWeight: 700,
                    fontSize: 14,
                    color: "primary.main",
                    lineHeight: "20px",
                    mb: 0.5,
                  }}
                >
                  {feature.title}
                </Typography>
                <Typography
                  sx={{
                    fontSize: 12,
                    color: "rgba(1,67,57,0.7)",
                    lineHeight: "19.5px",
                  }}
                >
                  {feature.description}
                </Typography>
              </Box>
            ))}
          </Box>

          {/* Stats Bar */}
          <Box
            sx={{
              display: "flex",
              borderRadius: "14px",
              border: "1px solid rgba(1,67,57,0.2)",
              background:
                "linear-gradient(173deg, rgba(1, 67, 57, 0.05) 0%, rgba(1, 67, 57, 0.1) 100%)",
              py: 2.5,
              px: 2.5,
              mb: 4,
            }}
          >
            {STATS.map((stat, index) => (
              <Box
                key={index}
                sx={{
                  flex: 1,
                  textAlign: "center",
                  ...(index === 1 && {
                    borderLeft: "1px solid rgba(1,67,57,0.2)",
                    borderRight: "1px solid rgba(1,67,57,0.2)",
                  }),
                }}
              >
                <Typography
                  sx={{
                    fontWeight: 700,
                    fontSize: 30,
                    lineHeight: "36px",
                    color: "primary.main",
                  }}
                >
                  {stat.value}
                </Typography>
                <Typography
                  sx={{
                    fontWeight: 600,
                    fontSize: 12,
                    color: "rgba(1,67,57,0.7)",
                    textTransform: "uppercase",
                    lineHeight: "16px",
                  }}
                >
                  {stat.label}
                </Typography>
              </Box>
            ))}
          </Box>

          {/* CTA Button */}
          <Button
            disableElevation
            fullWidth
            variant="contained"
            onClick={handleUpgrade}
            sx={{
              background:
                "linear-gradient(to right, #014339, rgba(1,67,57,0.9))",
              borderRadius: "14px",
              height: 60,
              fontSize: 18,
              fontWeight: 700,
              textTransform: "none",
              lineHeight: "28px",
              color: "white",
              "&:hover": {
                background:
                  "linear-gradient(to right, #012e27, rgba(1,67,57,0.95))",
              },
            }}
          >
            <Box
              component="span"
              className="fa-solid fa-wand-magic-sparkles"
              sx={{ mr: 1, fontSize: 16 }}
            />
            Upgrade to Autopilot
            <Box
              component="span"
              className="fa-solid fa-wand-magic-sparkles"
              sx={{ ml: 1, fontSize: 16 }}
            />
          </Button>

          {/* Pricing Footer */}
          <Typography
            sx={{
              textAlign: "center",
              mt: 2,
              fontSize: 14,
              lineHeight: "20px",
              color: "rgba(1,67,57,0.6)",
            }}
          >
            Starting at{" "}
            <Box
              component="span"
              sx={{ fontWeight: 700, color: "primary.main" }}
            >
              {priceDisplay}
            </Box>{" "}
            {" • Cancel anytime"}
          </Typography>
        </Box>
      </Box>
    </Box>
  );
}
